import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from '@/design-system/icons';
import type { CompanyFormData, Level } from '../models';
import { updateCompanyLevel, moveCompany } from '../api';
import { fetchCompanies } from '@/features/companies/api/companies';
import { useCompanyLevels } from '@/features/companies';
import type { Company } from '@/features/companies/types';
import { FormSection } from '@/components/FormSection';
import { SelectCompanyLevel } from './SelectCompanyLevel';

interface AdminSectionProps {
    formData: CompanyFormData;
    companyId: string;
    parentCompanyName?: string;
    parentCompanyId?: string;
    onLevelChange: (level: Level) => void;
    onParentCompanyChange?: (targetCompanyId: string) => void;
}

export function AdminSection({ formData, companyId, parentCompanyName, parentCompanyId, onLevelChange, onParentCompanyChange }: AdminSectionProps) {
    const { t } = useTranslation();
    const { data: levels = [] } = useCompanyLevels();
    const [isLevelModalOpen, setIsLevelModalOpen] = useState(false);
    const [isUpdatingLevel, setIsUpdatingLevel] = useState(false);

    const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
    const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
    const [isMoving, setIsMoving] = useState(false);
    const [moveTargets, setMoveTargets] = useState<Company[]>([]);
    const [isLoadingTargets, setIsLoadingTargets] = useState(false);

    const handleOpenLevelModal = () => {
        setIsLevelModalOpen(true);
    };

    const handleCloseLevelModal = () => {
        setIsLevelModalOpen(false);
    };

    const handleLevelSelect = async (level: Level) => {
        setIsUpdatingLevel(true);
        try {
            await updateCompanyLevel(companyId, level.id);
            onLevelChange(level);
            handleCloseLevelModal();
        } catch (error) {
            console.error('Failed to update company level:', error);
        } finally {
            setIsUpdatingLevel(false);
        }
    };

    const handleOpenMoveModal = async () => {
        setSelectedTargetId(null);
        setIsMoveModalOpen(true);
        setIsLoadingTargets(true);
        try {
            const response = await fetchCompanies({ page: 1, pageSize: 1000, stopAtCompany: companyId });
            setMoveTargets(response.data);
        } catch (error) {
            console.error('Failed to fetch move targets:', error);
        } finally {
            setIsLoadingTargets(false);
        }
    };

    const handleCloseMoveModal = () => {
        setSelectedTargetId(null);
        setIsMoveModalOpen(false);
        setMoveTargets([]);
    };

    const handleConfirmMove = async () => {
        if (selectedTargetId) {
            setIsMoving(true);
            try {
                await moveCompany(companyId, selectedTargetId);
                onParentCompanyChange?.(selectedTargetId);
                handleCloseMoveModal();
            } catch (error) {
                console.error('Failed to move company:', error);
            } finally {
                setIsMoving(false);
            }
        }
    };


    // Translated name of the currently set level, sourced from the shared levels cache.
    const translatedLevelName = formData.level
        ? levels.find((level) => level.id === formData.level!.id)?.name ?? formData.level.name
        : undefined;
    const currentLevelName = translatedLevelName ?? t('companyAdmin.form.noLevelSelected');

    return (
        <>
            <FormSection icon="settings" title={t('companyAdmin.form.adminSection')}>
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-onsurface">
                            {t('companyAdmin.form.level')}
                        </label>
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-onsurface">{currentLevelName}</span>
                            <button
                                type="button"
                                onClick={handleOpenLevelModal}
                                className="py-1.5 px-3 text-sm font-medium rounded-lg border border-border bg-surface text-onsurface hover:bg-surface-darker focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                                {t('companyAdmin.form.changeLevel')}
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-onsurface">
                            {t('companyAdmin.form.parentCompany')}
                        </label>
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-onsurface">
                                {parentCompanyName ?? t('companyAdmin.form.noParentCompany')}
                            </span>
                            <button
                                type="button"
                                onClick={handleOpenMoveModal}
                                className="py-1.5 px-3 text-sm font-medium rounded-lg border border-border bg-surface text-onsurface hover:bg-surface-darker focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                                {t('companyAdmin.form.changeParentCompany')}
                            </button>
                        </div>
                    </div>
                </div>
            </FormSection>

            <SelectCompanyLevel
                isOpen={isLevelModalOpen}
                companyId={companyId}
                selectedLevelId={formData.level?.id}
                isUpdating={isUpdatingLevel}
                onClose={handleCloseLevelModal}
                onSelect={handleLevelSelect}
            />

            {isMoveModalOpen && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-50"
                        onClick={handleCloseMoveModal}
                        aria-hidden="true"
                    />
                    <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
                        <div
                            className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <header className="flex items-center justify-between px-4 py-3 border-b border-border">
                                <h3 className="text-lg font-semibold text-onsurface">
                                    {t('companyAdmin.form.changeParentCompanyTitle')}
                                </h3>
                                <button
                                    type="button"
                                    onClick={handleCloseMoveModal}
                                    className="size-8 inline-flex justify-center items-center rounded-md border border-border bg-surface text-muted hover:bg-surface-darker focus:outline-none"
                                >
                                    <X className="size-4" />
                                </button>
                            </header>

                            <div className="p-4">
                                <p className="text-sm text-muted mb-4">
                                    {t('companyAdmin.form.changeParentCompanyWarning')}
                                </p>

                                <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
                                    {isLoadingTargets ? (
                                        <div className="text-sm text-muted text-center py-4">
                                            {t('companyAdmin.form.loading')}
                                        </div>
                                    ) : moveTargets.length === 0 ? (
                                        <div className="text-sm text-muted text-center py-4">
                                            {t('companyAdmin.form.noMoveTargets')}
                                        </div>
                                    ) : (
                                        moveTargets.map((target) => {
                                            if (target.id === parentCompanyId){
                                                return null;
                                            }
                                            return (
                                            <button
                                                key={target.id}
                                                type="button"
                                                onClick={() => setSelectedTargetId(target.id)}
                                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                                    selectedTargetId === target.id
                                                        ? 'bg-primary text-onprimary'
                                                        : 'bg-surface-darker text-onsurface hover:bg-border'
                                                }`}
                                            >
                                                {target.name}
                                            </button>
                                        )})
                                    )}
                                </div>
                            </div>

                            <footer className="flex justify-end gap-3 px-4 py-3 border-t border-border">
                                <button
                                    type="button"
                                    onClick={handleCloseMoveModal}
                                    className="py-2 px-4 text-sm font-medium rounded-lg border border-border bg-surface text-onsurface hover:bg-surface-darker focus:outline-none"
                                >
                                    {t('companyAdmin.form.cancel')}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmMove}
                                    disabled={!selectedTargetId || isMoving}
                                    className="py-2 px-4 text-sm font-medium rounded-lg border border-transparent bg-primary text-onprimary hover:bg-primary-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-primary"
                                >
                                    {isMoving ? t('companyAdmin.form.saving') : t('companyAdmin.form.confirm')}
                                </button>
                            </footer>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
